import React, { useState } from 'react';
import { RouteSelection } from '../types';

interface RouteSelectorProps {
  availableCities: string[];
  availableRoutes: string[];
  availablePreprocessedRoutes: number[];
  onRoutesSelected: (selection: RouteSelection) => void;
}

const RouteSelector: React.FC<RouteSelectorProps> = ({ availableCities, availableRoutes, availablePreprocessedRoutes, onRoutesSelected }) => {
  const [selectedCity, setSelectedCity] = useState<string>("St. Gallen"); // Default city
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [preprocessed, setPreprocessed] = useState<boolean>(false);
  const [randomRouteCount, setRandomRouteCount] = useState<number>(0);
  const [selectedPreprocessedRouteCount, setSelectedPreprocessedRouteCount] = useState<number>(0);
  const [selectedOption, setSelectedOption] = useState<'preprocessed' | 'custom'>('preprocessed');

  const handleCheckboxChange = (route: string) => {
    setSelectedRoutes((prevSelectedRoutes) =>
      prevSelectedRoutes.includes(route)
        ? prevSelectedRoutes.filter((r) => r !== route)
        : [...prevSelectedRoutes, route]
    );
  };

  const handleRandomRouteCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value)) {
      setRandomRouteCount(value);
    }
  };

  const handleSubmit = () => {

    const selection: RouteSelection = preprocessed
      ? {
        city: selectedCity,
        routes: [],
        routeCount: selectedPreprocessedRouteCount,
        isPreprocessed: true,
      }
      : {
        city: selectedCity,
        routes: selectedRoutes,
        routeCount: randomRouteCount,
        isPreprocessed: false,
      };

    onRoutesSelected(selection);
  };

  const handleOptionClick = (option: 'preprocessed' | 'custom') => {
    if (selectedOption === option) {
      setSelectedOption('preprocessed'); // Default to preprocessed when closing
    } else {
      setSelectedOption(option);
      if (option === 'preprocessed') {
        setSelectedRoutes([]);
        setRandomRouteCount(0);
      } else {
        setSelectedPreprocessedRouteCount(0);
        setPreprocessed(false);
      }
    }
  };

  return (
    <div className="route-selector">
      <div className="city-selector">
        <h3>Select a City:</h3>
        <select
          value={selectedCity}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setSelectedCity(e.target.value);
          }}
        >
          {availableCities.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      <div className="option-sections">
        <div className={`option-section ${selectedOption === 'preprocessed' ? 'expanded' : ''}`}>
          <button
            className="section-header"
            onClick={() => handleOptionClick('preprocessed')}
          >
            <h3>Preprocessed Routes {selectedOption === 'preprocessed' ? '▼' : '▶'}</h3>
          </button>

          {selectedOption === 'preprocessed' && (
            <div className="section-content">
              <label className="inline-label">
                Select number of random routes:
                <select
                  className="number-select"
                  value={selectedPreprocessedRouteCount}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const count = parseInt(e.target.value, 10);
                    setSelectedPreprocessedRouteCount(count);
                    setPreprocessed(true);
                    setSelectedRoutes([]);
                    setRandomRouteCount(0);
                  }}
                >
                  {availablePreprocessedRoutes.map((count) => (
                    <option key={count} value={count}>{count}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        <div className={`option-section ${selectedOption === 'custom' ? 'expanded' : ''}`}>
          <button
            className="section-header"
            onClick={() => handleOptionClick('custom')}
          >
            <h3>Custom Request {selectedOption === 'custom' ? '▼' : '▶'}</h3>
          </button>


          {selectedOption === 'custom' && (
            <div className="section-content">
              <p className="warning-message">Note: Google and Graphhopper routes are disabled in this demo version.</p>
              <h4>Select specific providers:</h4>
              <div className="checkbox-group">
                {availableRoutes.map((route) => (
                  <div key={route} className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      id={route}
                      checked={selectedRoutes.includes(route)}
                      onChange={() => {
                        handleCheckboxChange(route);
                        setPreprocessed(false);
                        setSelectedPreprocessedRouteCount(0);
                      }}
                    />
                    <label htmlFor={route}>{route}</label>
                  </div>
                ))}
              </div>
              <div className="input-container">
                <label className="inline-label">
                  Select number of random routes:
                  <input
                    className="number-input"
                    type="number"
                    value={randomRouteCount}
                    onChange={(event) => {
                      handleRandomRouteCountChange(event);
                      setPreprocessed(false);
                      setSelectedPreprocessedRouteCount(0);
                    }}
                    min="0"
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      <button onClick={handleSubmit} disabled={!preprocessed && selectedRoutes.length === 0 && randomRouteCount === 0 && selectedCity === ""}>
        Submit
      </button>
    </div>
  );
};

export default RouteSelector;
